import { useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { buildUserAuthState } from './scopedRelationshipResolver';
import { canResolve, type Capability, type ScopedContext } from './scopedCapabilityResolver';
import {
  authorityProjectionSession,
  useAuthorityProjectionState,
} from './authorityProjectionStore';

/**
 * One browser authorization entry point across Demo and connected modes.
 * Demo uses explicit fixture facts; connected mode consumes only the
 * actor/scope-validated server projection.
 */
export function useScopedAuthority() {
  const auth = useAuth();
  const user = auth?.user;
  const authorityProjection = useAuthorityProjectionState();

  const can = useCallback((capability: Capability, context?: ScopedContext): boolean => {
    if (!user) return false;
    if (auth?.isDemoMode) {
      return canResolve(buildUserAuthState(user), capability, context);
    }
    if (authorityProjection.status !== 'confirmed'
      || authorityProjection.projection.actor.userId !== user.id) {
      return false;
    }
    return authorityProjectionSession.can(capability, context);
  }, [auth?.isDemoMode, authorityProjection, user]);

  return { can, authorityProjection } as const;
}
